import { msg } from "@lingui/core/macro";
import {
  addComboItemMutation,
  createCategoryMutation,
  createComboMutation,
  createModifierGroupMutation,
  createModifierOptionMutation,
  createProductMutation,
  createVariantMutation,
  deleteComboMutation,
  deleteProductMutation,
  deleteVariantMutation,
  formatMoney,
  getCategoriesQuery,
  getCombosQuery,
  getModifierGroupsQuery,
  getProductModifierGroupsQuery,
  getProductsQuery,
  getVariantsQuery,
  linkProductModifierGroupMutation,
  parseMoneyToCents,
  queryKeys,
  restoreTrashItemMutation,
  updateProductMutation,
} from "@comandero/shared";
import type {
  Branch,
  Combo,
  MenuCategory,
  MenuProduct,
  ModifierGroup,
  Organization,
  ProductModifierGroup,
  ProductVariant,
} from "@comandero/powersync";
import { createPowerSyncQuery } from "@comandero/shared/solid";
import { createSignal, For, Show } from "solid-js";
import { Button } from "@/components/ui/button";
import { Select, toSelectOptions } from "@/components/ui/select";
import { TextField } from "@fleet/ui";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useI18n } from "@/providers/i18n-provider";
import { useUndo } from "@/lib/undo";
import { EntityHistoryDialog } from "@/features/activity/components/entity-history-dialog";

interface MenuManagerProps {
  org: () => Organization | null;
  branch: () => Branch | null;
}

export function MenuManager(props: MenuManagerProps) {
  const { t } = useI18n();
  const drizzle = useDrizzle();
  const { db } = usePowerSyncContext();
  const { registerUndo } = useUndo();

  const [catName, setCatName] = createSignal("");
  const [prodName, setProdName] = createSignal("");
  const [prodPrice, setProdPrice] = createSignal("");
  const [prodCat, setProdCat] = createSignal("");
  const [groupName, setGroupName] = createSignal("");
  const [optName, setOptName] = createSignal("");
  const [optPrice, setOptPrice] = createSignal("");
  const [optGroup, setOptGroup] = createSignal("");
  // Ref to creation input: modifier groups Select, when empty,
  // focuses field creating its options instead of dead combo.
  let groupNameInput: HTMLInputElement | undefined;
  // F4: variants (per selected product) and combos.
  const [variantProduct, setVariantProduct] = createSignal("");
  const [variantName, setVariantName] = createSignal("");
  const [variantPrice, setVariantPrice] = createSignal("");
  const [comboName, setComboName] = createSignal("");
  const [comboPrice, setComboPrice] = createSignal("");
  // Inline product editing + version history (VERSION-HISTORY).
  const [editingProduct, setEditingProduct] = createSignal<string | null>(null);
  const [editName, setEditName] = createSignal("");
  const [editPrice, setEditPrice] = createSignal("");
  const [historyProduct, setHistoryProduct] = createSignal<string | null>(null);

  const orgId = (): string | null => props.org()?.id ?? null;

  const categories = createPowerSyncQuery<MenuCategory>({
    queryKey: () => queryKeys.comandero.categories(orgId() ?? "none"),
    queryFn: () => {
      const d = drizzle();
      const id = orgId();
      return d && id ? getCategoriesQuery(d, id) : null;
    },
    db,
    enabled: () => Boolean(orgId()),
  });

  const products = createPowerSyncQuery<MenuProduct>({
    queryKey: () => queryKeys.comandero.products(orgId() ?? "none"),
    queryFn: () => {
      const d = drizzle();
      const id = orgId();
      return d && id ? getProductsQuery(d, id) : null;
    },
    db,
    enabled: () => Boolean(orgId()),
  });

  const groups = createPowerSyncQuery<ModifierGroup>({
    queryKey: () => queryKeys.comandero.modifierGroups(orgId() ?? "none"),
    queryFn: () => {
      const d = drizzle();
      const id = orgId();
      return d && id ? getModifierGroupsQuery(d, id) : null;
    },
    db,
    enabled: () => Boolean(orgId()),
  });

  const variants = createPowerSyncQuery<ProductVariant>({
    queryKey: () => queryKeys.comandero.variants(variantProduct() || "none"),
    queryFn: () => {
      const d = drizzle();
      const pid = variantProduct();
      return d && pid ? getVariantsQuery(d, pid) : null;
    },
    db,
    enabled: () => Boolean(variantProduct()),
  });

  const combos = createPowerSyncQuery<Combo>({
    queryKey: () => queryKeys.comandero.combos(orgId() ?? "none"),
    queryFn: () => {
      const d = drizzle();
      const id = orgId();
      return d && id ? getCombosQuery(d, id) : null;
    },
    db,
    enabled: () => Boolean(orgId()),
  });

  async function addVariant(e: Event) {
    e.preventDefault();
    const d = drizzle();
    const id = orgId();
    const pid = variantProduct();
    if (!d || !id || !pid || !variantName().trim()) {
      return;
    }
    await createVariantMutation(d, id, pid, variantName(), parseMoneyToCents(variantPrice()));
    setVariantName("");
    setVariantPrice("");
  }

  async function removeVariant(id: string) {
    const d = drizzle();
    if (!d) {
      return;
    }
    try {
      await deleteVariantMutation(d, id);
    } catch (err: unknown) {
      console.error("Error deleting variant:", err);
    }
  }

  async function addCombo(e: Event) {
    e.preventDefault();
    const d = drizzle();
    const id = orgId();
    if (!d || !id || !comboName().trim()) {
      return;
    }
    const comboId = await createComboMutation(d, id, comboName(), parseMoneyToCents(comboPrice()));
    // Links all existing products as combo items (informational +
    // for stock deduction on charge). In a real editor chosen by hand.
    for (const p of products.data ?? []) {
      await addComboItemMutation(d, id, comboId, p.id, 1);
    }
    setComboName("");
    setComboPrice("");
  }

  async function removeCombo(id: string) {
    const d = drizzle();
    if (!d) {
      return;
    }
    try {
      await deleteComboMutation(d, id);
    } catch (err: unknown) {
      console.error("Error deleting combo:", err);
    }
  }

  async function addCategory(e: Event) {
    e.preventDefault();
    const d = drizzle();
    const id = orgId();
    if (!d || !id || !catName().trim()) {
      return;
    }
    await createCategoryMutation(d, id, catName());
    setCatName("");
  }

  async function addProduct(e: Event) {
    e.preventDefault();
    const d = drizzle();
    const id = orgId();
    if (!d || !id || !prodName().trim()) {
      return;
    }
    await createProductMutation(d, id, prodName(), parseMoneyToCents(prodPrice()), {
      categoryId: prodCat() || null,
      taxRate: 0,
    });
    setProdName("");
    setProdPrice("");
  }

  async function addGroup(e: Event) {
    e.preventDefault();
    const d = drizzle();
    const id = orgId();
    if (!d || !id || !groupName().trim()) {
      return;
    }
    await createModifierGroupMutation(d, id, groupName(), { maxSelect: 1 });
    setGroupName("");
  }

  async function addOption(e: Event) {
    e.preventDefault();
    const d = drizzle();
    const id = orgId();
    // If no group chosen explicitly, uses first available (convenient
    // when single group and robust against <select> race).
    const groupId = optGroup() || groups.data?.[0]?.id || "";
    if (!d || !id || !groupId || !optName().trim()) {
      return;
    }
    await createModifierOptionMutation(d, id, groupId, optName(), parseMoneyToCents(optPrice()));
    setOptName("");
    setOptPrice("");
  }

  async function linkGroup(productId: string, groupId: string) {
    const d = drizzle();
    const id = orgId();
    if (!d || !id || !groupId) {
      return;
    }
    await linkProductModifierGroupMutation(d, id, productId, groupId);
  }

  function startEditProduct(product: MenuProduct) {
    setEditingProduct(product.id);
    setEditName(product.name ?? "");
    setEditPrice(formatMoney(product.price));
  }

  async function saveProduct(product: MenuProduct, e: Event) {
    e.preventDefault();
    const d = drizzle();
    if (!d) {
      return;
    }
    // Only fields that actually change: each version in history
    // reflects exact change (no noise from identical fields).
    const updates: { name?: string; price?: number } = {};
    const name = editName().trim();
    if (name && name !== product.name) {
      updates.name = name;
    }
    const price = parseMoneyToCents(editPrice());
    if (price !== product.price) {
      updates.price = price;
    }
    if (Object.keys(updates).length > 0) {
      try {
        await updateProductMutation(d, product.id, updates);
      } catch (err: unknown) {
        console.error("Error updating product:", err);
        return;
      }
    }
    setEditingProduct(null);
  }

  async function removeProduct(productId: string) {
    const d = drizzle();
    if (!d) {
      return;
    }
    const name = (products.data ?? []).find((p) => p.id === productId)?.name ?? "";
    // L2: soft-delete to trash + L1: immediate undo (restore).
    try {
      await deleteProductMutation(d, productId);
    } catch (err: unknown) {
      console.error("Error deleting product:", err);
      return;
    }
    registerUndo(t(msg`Product moved to trash: {name}`, { name }), async () => {
      try {
        await restoreTrashItemMutation(d, "menu_product", productId);
      } catch (err: unknown) {
        console.error("Error restoring product from trash:", err);
      }
    });
  }

  return (
    <div class="flex flex-col gap-8">
      {/* Categories */}
      <section>
        <h2 class="mb-3 text-lg font-semibold">{t(msg`Categories`)}</h2>
        <form class="mb-3 flex gap-2" onSubmit={addCategory}>
          <TextField
            data-testid="category-name-input"
            placeholder={t(msg`New category`)}
            value={catName()}
            onInput={(e) => setCatName(e.currentTarget.value)}
          />
          <Button data-testid="add-category-btn" type="submit">
            {t(msg`Add`)}
          </Button>
        </form>
        <ul class="flex flex-wrap gap-2">
          <For each={categories.data}>
            {(c) => (
              <li
                class="rounded-md border border-border/40 px-3 py-1 text-sm"
                data-testid="category-item"
              >
                {c.name}
              </li>
            )}
          </For>
        </ul>
      </section>

      {/* Products */}
      <section>
        <h2 class="mb-3 text-lg font-semibold">{t(msg`Products`)}</h2>
        <form class="mb-3 flex flex-wrap gap-2" onSubmit={addProduct}>
          <TextField
            data-testid="product-name-input"
            placeholder={t(msg`Product name`)}
            value={prodName()}
            onInput={(e) => setProdName(e.currentTarget.value)}
          />
          <TextField
            data-testid="product-price-input"
            placeholder={t(msg`Price`)}
            inputmode="decimal"
            value={prodPrice()}
            onInput={(e) => setProdPrice(e.currentTarget.value)}
          />
          <Select
            data-testid="product-category-select"
            aria-label={t(msg`Category`)}
            value={prodCat()}
            onChange={setProdCat}
            options={[
              { value: "", label: t(msg`No category`) },
              ...toSelectOptions(
                categories.data,
                (c) => c.id,
                (c) => c.name,
              ),
            ]}
          />
          <Button data-testid="add-product-btn" type="submit">
            {t(msg`Add product`)}
          </Button>
        </form>
        <ul class="flex flex-col gap-2">
          <For each={products.data}>
            {(p) => {
              const linkedGroups = createPowerSyncQuery<ProductModifierGroup>({
                queryKey: () => queryKeys.comandero.productModifierGroups(p.id),
                queryFn: () => {
                  const d = drizzle();
                  const id = orgId();
                  return d && id ? getProductModifierGroupsQuery(d, id, p.id) : null;
                },
                db,
                enabled: () => Boolean(orgId()),
              });

              return (
                <li
                  class="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border/40 px-3 py-2 text-sm"
                  data-testid="product-item"
                >
                  <Show
                    when={editingProduct() !== p.id}
                    fallback={
                      <form
                        class="flex flex-1 flex-wrap items-center gap-2"
                        onSubmit={(e) => void saveProduct(p, e)}
                      >
                        <TextField
                          data-testid="edit-product-name-input"
                          aria-label={t(msg`Product name`)}
                          value={editName()}
                          onInput={(e) => setEditName(e.currentTarget.value)}
                        />
                        <TextField
                          data-testid="edit-product-price-input"
                          aria-label={t(msg`Price`)}
                          inputmode="decimal"
                          value={editPrice()}
                          onInput={(e) => setEditPrice(e.currentTarget.value)}
                        />
                        <Button data-testid="save-product-btn" type="submit" size="sm">
                          {t(msg`Save`)}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          type="button"
                          data-testid="cancel-edit-product-btn"
                          onClick={() => setEditingProduct(null)}
                        >
                          {t(msg`Cancel`)}
                        </Button>
                      </form>
                    }
                  >
                    <span data-testid="product-row-name">{p.name}</span>
                    <span class="text-muted-foreground" data-testid="product-row-price">
                      {formatMoney(p.price)}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid="edit-product-btn"
                      onClick={() => startEditProduct(p)}
                    >
                      {t(msg`Edit`)}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid="product-history-btn"
                      onClick={() => setHistoryProduct(p.id)}
                    >
                      {t(msg`History`)}
                    </Button>
                  </Show>
                  <For each={linkedGroups.data}>
                    {(link) => (
                      <span
                        class="rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground"
                        data-testid="product-linked-group"
                      >
                        {groups.data?.find((g) => g.id === link.modifier_group_id)?.name ?? ""}
                      </span>
                    )}
                  </For>
                  <Select
                    data-testid="product-link-group"
                    aria-label={t(msg`Link modifier group`)}
                    placeholder={t(msg`Link modifier group…`)}
                    size="sm"
                    value=""
                    onChange={(v) => {
                      if (v) {
                        void linkGroup(p.id, v);
                      }
                    }}
                    options={toSelectOptions(
                      groups.data,
                      (g) => g.id,
                      (g) => g.name,
                    )}
                    emptyAction={{
                      label: t(msg`Create a modifier group`),
                      description: t(msg`No modifier groups yet`),
                      onSelect: () => groupNameInput?.focus(),
                    }}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid="delete-product-btn"
                    onClick={() => void removeProduct(p.id)}
                  >
                    {t(msg`Delete`)}
                  </Button>
                </li>
              );
            }}
          </For>
        </ul>
      </section>

      {/* Modifier groups */}
      <section>
        <h2 class="mb-3 text-lg font-semibold">{t(msg`Modifier groups`)}</h2>
        <form class="mb-3 flex gap-2" onSubmit={addGroup}>
          <TextField
            ref={groupNameInput}
            data-testid="group-name-input"
            placeholder={t(msg`Group name (e.g. Doneness)`)}
            value={groupName()}
            onInput={(e) => setGroupName(e.currentTarget.value)}
          />
          <Button data-testid="add-group-btn" type="submit">
            {t(msg`Add group`)}
          </Button>
        </form>
        <Show when={(groups.data ?? []).length > 0}>
          <form class="mb-3 flex flex-wrap gap-2" onSubmit={addOption}>
            <Select
              data-testid="option-group-select"
              aria-label={t(msg`Modifier group`)}
              placeholder={t(msg`Select group`)}
              value={optGroup()}
              onChange={setOptGroup}
              options={toSelectOptions(
                groups.data,
                (g) => g.id,
                (g) => g.name,
              )}
            />
            <TextField
              data-testid="option-name-input"
              placeholder={t(msg`Option (e.g. Medium)`)}
              value={optName()}
              onInput={(e) => setOptName(e.currentTarget.value)}
            />
            <TextField
              data-testid="option-price-input"
              placeholder={t(msg`Extra price`)}
              inputmode="decimal"
              value={optPrice()}
              onInput={(e) => setOptPrice(e.currentTarget.value)}
            />
            <Button data-testid="add-option-btn" type="submit">
              {t(msg`Add option`)}
            </Button>
          </form>
        </Show>
        <ul class="flex flex-wrap gap-2">
          <For each={groups.data}>
            {(g) => <li class="rounded-md border border-border/40 px-3 py-1 text-sm">{g.name}</li>}
          </For>
        </ul>
      </section>

      {/* Variants (F4): tamaños/opciones con precio propio por producto */}
      <section>
        <h2 class="mb-3 text-lg font-semibold">{t(msg`Variants`)}</h2>
        <form class="mb-3 flex flex-wrap gap-2" onSubmit={addVariant}>
          <Select
            data-testid="variant-product-select"
            aria-label={t(msg`Product`)}
            placeholder={t(msg`Select product`)}
            value={variantProduct()}
            onChange={setVariantProduct}
            options={toSelectOptions(
              products.data,
              (p) => p.id,
              (p) => p.name,
            )}
          />
          <TextField
            data-testid="variant-name-input"
            placeholder={t(msg`Variant (e.g. Large)`)}
            value={variantName()}
            onInput={(e) => setVariantName(e.currentTarget.value)}
          />
          <TextField
            data-testid="variant-price-input"
            placeholder={t(msg`Variant price`)}
            inputmode="decimal"
            value={variantPrice()}
            onInput={(e) => setVariantPrice(e.currentTarget.value)}
          />
          <Button data-testid="add-variant-btn" type="submit">
            {t(msg`Add variant`)}
          </Button>
        </form>
        <Show when={variantProduct()}>
          <ul class="flex flex-wrap gap-2">
            <For each={variants.data}>
              {(v) => (
                <li
                  class="flex items-center gap-2 rounded-md border border-border/40 px-3 py-1 text-sm"
                  data-testid="variant-item"
                >
                  <span>
                    {v.name} — {formatMoney(v.price)}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid="delete-variant-btn"
                    onClick={() => void removeVariant(v.id)}
                  >
                    {t(msg`Delete`)}
                  </Button>
                </li>
              )}
            </For>
          </ul>
        </Show>
      </section>

      {/* Combos (F4): menú combinado a precio especial */}
      <section>
        <h2 class="mb-3 text-lg font-semibold">{t(msg`Combos`)}</h2>
        <form class="mb-3 flex flex-wrap gap-2" onSubmit={addCombo}>
          <TextField
            data-testid="combo-name-input"
            placeholder={t(msg`Combo name`)}
            value={comboName()}
            onInput={(e) => setComboName(e.currentTarget.value)}
          />
          <TextField
            data-testid="combo-price-input"
            placeholder={t(msg`Combo price`)}
            inputmode="decimal"
            value={comboPrice()}
            onInput={(e) => setComboPrice(e.currentTarget.value)}
          />
          <Button data-testid="add-combo-btn" type="submit">
            {t(msg`Add combo`)}
          </Button>
        </form>
        <ul class="flex flex-col gap-2">
          <For each={combos.data}>
            {(c) => (
              <li
                class="flex items-center justify-between gap-3 rounded-md border border-border/40 px-3 py-2 text-sm"
                data-testid="combo-item"
              >
                <span data-testid="combo-row-name">{c.name}</span>
                <span class="text-muted-foreground" data-testid="combo-row-price">
                  {formatMoney(c.price)}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid="delete-combo-btn"
                  onClick={() => void removeCombo(c.id)}
                >
                  {t(msg`Delete`)}
                </Button>
              </li>
            )}
          </For>
        </ul>
      </section>

      {/* VERSION-HISTORY: historial/versiones del producto seleccionado. */}
      <Show when={historyProduct()}>
        {(productId) => (
          <EntityHistoryDialog
            open={Boolean(historyProduct())}
            onOpenChange={(open) => {
              if (!open) {
                setHistoryProduct(null);
              }
            }}
            entityType="menu_product"
            entityId={productId()}
          />
        )}
      </Show>
    </div>
  );
}
